'use client';

import { useEffect, type RefObject } from 'react';

/** Room left below the sidebar once its end has come into view. */
const BOTTOM_GAP = 24;

/**
 * Sticky for a sidebar taller than the window: it scrolls along with the page
 * until its end is in view and only then sticks; scrolling back up, it follows
 * again until its head is in view. Every scroll step moves its sticky `top` by
 * the same amount, held between those two resting places — for a sidebar that
 * fits, both are the `--sticky-top` from the stylesheet and it simply sticks.
 */
export function useStickySidebar(ref: RefObject<HTMLElement | null>) {
    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const stickyTop = parseFloat(getComputedStyle(element).getPropertyValue('--sticky-top')) || 0;
        let top = stickyTop;
        let lastScrollY = window.scrollY;
        let frame = 0;

        const update = () => {
            frame = 0;
            const lowest = Math.min(stickyTop, window.innerHeight - element.offsetHeight - BOTTOM_GAP);
            top = Math.min(stickyTop, Math.max(lowest, top - (window.scrollY - lastScrollY)));
            lastScrollY = window.scrollY;
            element.style.top = `${top}px`;
        };

        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(update);
        };

        // Also catches the sidebar growing or shrinking, e.g. the model list opening.
        const observer = new ResizeObserver(schedule);
        observer.observe(element);
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);

        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
        };
    }, [ref]);
}
